export const JOB_ACTIVITY_TYPES = ['delivery', 'pickup'] as const;
export const TIGHT_BUFFER_THRESHOLD_MINUTES = 30;

export const THREAD_ITEM_TYPES = {
  PROMPT: 'prompt',
  RESPONSE: 'response',
} as const;

export const RESPONSE_VARIANTS = {
  READY: 'ready',
  WARNING: 'warning',
  ROUTING: 'routing',
  PLAIN: 'plain',
} as const;

export const RANDOM_RESPONSE_VARIANTS = [
  RESPONSE_VARIANTS.READY,
  RESPONSE_VARIANTS.WARNING,
  RESPONSE_VARIANTS.ROUTING,
] as const;

export const MAX_UPLOAD_FILE_SIZE_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_FILE_EXTENSIONS = ['.csv', '.txt'];
export const ACCEPTED_MIME_TYPES = ['text/csv', 'text/plain'];

export type PromptKey =
  | 'generic'
  | 'capacityOverview'
  | 'truckOffline'
  | 'addVehicle'
  | 'suggestSwap'
  | 'applySwap'
  | 'showAlternatives'
  | 'looksGood'
  | 'fromLibrary'
  | 'oneOff'
  | 'uploadCsv';

export type ResponseVariant = typeof RESPONSE_VARIANTS[keyof typeof RESPONSE_VARIANTS];

export type TimeWindow = [string, string];

export type RoutingAiChatTranslation = {
  askButton: string;
  title: string;
  fleetBadge: string;
  closeLabel: string;
  readyLabel: string;
  noPlanYet: string;
  noPlanActionReply: string;
  noInsight: string;
  genericReply: string;
  truckOfflineCard: string;
  addVehicleCard: string;
  fromLibraryReply: string;
  oneOffReply: string;
  swapReplyNoInsight: string;
  showAlternativesReply: string;
  looksGoodReply: string;
  capacityCard: string;
  swapCard: string;
  applySwapReply: string;
  placeholder: string;
  sendLabel: string;
  tourSingular: string;
  tourPlural: string;
  summaryTemplate: string;
  insightTemplate: string;
  uploadCsv: {
    uploading: string;
    uploadedFileTemplate: string;
    success: string;
    successTemplate: string;
    error: string;
    fileTooLarge: string;
    readError: string;
    error400: string;
    error401: string;
    error413: string;
    error415: string;
    dropPrompt: string;
    invalidFile: string;
  };
  chatThinking: string;
  chatSubagentWorking: string;
  chatStreamError: string;
  chatStreamError400: string;
  chatStreamError401: string;
  chatStreamError404: string;
  chatCapError: string;
  workspaceCreateError: string;
  workspaceSessionUnavailable: string;
  workspaceRetryHint: string;
  restartLabel: string;
  agentTrace: string;
  agentTraceRunning: string;
  cardLabels: {
    routingAi: string;
    headsUp: string;
    ready: string;
  };
  cardActions: {
    suggestSwap: string;
    looksGood: string;
    retryConnection: string;
  };
  quickActions: {
    capacityOverview: string;
    truckOffline: string;
    addVehicle: string;
    uploadCsv: string;
  };
  promptTitles: {
    suggestSwap: string;
    suggestSwapFallback: string;
    lockPlan: string;
    applySwap: string;
    showAlternatives: string;
    fromLibrary: string;
    oneOff: string;
  };
  responseActions: {
    fromLibrary: string;
    oneOff: string;
    applySwap: string;
    showAlternatives: string;
  };
};

export type UnitsTranslation = {
  time: {
    hour: string;
    minute: string;
  };
};

export type JobPlace = {
  times?: TimeWindow[];
};

export type JobTask = {
  places?: JobPlace[];
};

export type Job = {
  id?: string;
  tasks?: {
    deliveries?: JobTask[];
    pickups?: JobTask[];
  };
};

export type StopActivity = {
  type?: string;
  jobId?: string;
};

export type TourStop = {
  time?: {
    arrival?: string;
  };
  activities?: StopActivity[];
};

export type Tour = {
  stops?: TourStop[];
};

export type TourData = {
  tours?: Tour[];
  unassigned?: unknown[];
  statistic?: {
    cost?: number;
    duration?: number;
  };
  plan?: {
    jobs?: Job[];
  };
};

export type SolutionRequest = {
  iterations: Array<{
    request?: unknown;
    response?: unknown;
    isProcessing?: boolean;
  }>;
};

export type SolutionState = {
  requests?: SolutionRequest[];
  show: number;
};

export type UserState = {
  openPanel?: string;
  currency?: string;
  aiChatWorkspaceId?: string | null;
};

export type RootState = {
  solution: SolutionState;
  user: UserState;
};

export type TightestStop = {
  slackMinutes: number;
  stopNumber: number;
  tourNumber: number;
  windowStart: string;
  windowEnd: string;
};

export type Summary = {
  hasPlan: boolean;
  key: string;
  assignedOrders?: number;
  totalOrders?: number;
  totalCost?: string;
  totalDuration?: string;
  tightestStop?: TightestStop | null;
  tourCount?: number;
  tourLabel?: string;
  avgOrders?: string;
  maxOrders?: number;
  rebalancedOrders?: string;
};

export type CardAction = {
  key: PromptKey | 'retryConnection';
  label: string;
  secondary?: boolean;
};

export type TraceStepKind = 'thought' | 'tool_call' | 'observation' | 'action';

export type TraceStep = {
  id: number;
  kind: TraceStepKind;
  label: string;
  detail?: string;
};

export type ResponseCardData = {
  variant: ResponseVariant;
  label?: string;
  body: string;
  actions: CardAction[];
  traceSteps?: TraceStep[];
};

export type PromptThreadItem = {
  id: number | string;
  type: typeof THREAD_ITEM_TYPES.PROMPT;
  text: string;
};

export type ResponseThreadItem = {
  id: number | string;
  type: typeof THREAD_ITEM_TYPES.RESPONSE;
  streaming?: boolean;
  progress?: number;
} & ResponseCardData;

export type ThreadItem = PromptThreadItem | ResponseThreadItem;

export type ArtifactRef = {
  artifact: string;
  version: number;
};

export type ArtifactInfo = {
  ref: ArtifactRef;
  who?: string;
  what?: string;
  why?: string;
};

export type ArtifactInfoDataResponse = {
  data?: ArtifactInfo;
};

export type RoutingAIChatProps = {
  translations: {
    routingAiChat: RoutingAiChatTranslation;
    units: UnitsTranslation;
  };
};

export type StyledSparkleProps = {
  sparkleColor?: string;
};

export type StyledCardButtonProps = {
  secondary?: boolean;
  selected?: boolean;
};

export type StyledResponseVariantProps = {
  variant: ResponseVariant;
};
